import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Carousel } from 'react-responsive-carousel';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import Zoom from 'react-medium-image-zoom';
import { Link } from 'react-router-dom';

import { productPropTypes } from 'helpers/propTypes';
import { getCookie, setCookie } from 'helpers/cookies';

import DescriptionItem from 'components/commons/DescriptionItem';
import QuantitySelector from 'components/commons/QuantitySelector';

import 'react-responsive-carousel/lib/styles/carousel.min.css';
import 'react-medium-image-zoom/dist/styles.css';
import './ProductTextil.scss';

const ProductTextil = (props) => {
  const {
    images,
    item,
    onProductClick,
    onProductCopyClick,
    onQuantityChange,
    singleProductFullUrl,
    cartItemDescriptions,
    colors,
    combinations,
    wholesale,
    setWholesale,
    productCart,
    productCartSub,
    cartItemSubDescriptions,
    handleColorSelect,
    talles,
    combinationsTalle,
    selectedColor,
    getValidPriceRange,
    show,
    onClose,
    cuotasMP,
    onOpenPaymentModal,
    showMPButton,
  } = props;

  const [active, setActive] = useState(0);
  let unitLabel;
  switch(item?.unit_type) {
    case 'kg':
      unitLabel = 'Kgs';
      break;
    case 'unidad':
      unitLabel = 'Unidades';
      break;
    case 'metro':
      unitLabel = 'Metros';
      break;
    default:
      unitLabel = 'Metros';
  }

  const photoList = useMemo(
    () => (images && images.length ? images : [item?.image1].filter(Boolean)),
    [images, item?.image1]
  );

  const hasInstallments = cuotasMP && cuotasMP.length > 0;

  const renderTotal = () => {
    const allItems = [...cartItemDescriptions, ...cartItemSubDescriptions].filter(
      (itemDesc) => itemDesc.wholesale?.id === wholesale.id
    );
  
    if (!allItems.length) return null;
  
    const totalQty = allItems.reduce((sum, desc) => sum + (Number(desc.quantity) || 0), 0);
  
    // Armar los tramos desde las descripciones con medida
    const tiers = (item.stock_product_descriptions || [])
      .filter((desc) => {
        const name = desc.stock_descriptions?.name;
        return name && !isNaN(parseFloat(name));
      })
      .map((desc) => ({
        min: parseFloat(desc.stock_descriptions.name),
        price: desc.price
      }))
      .sort((a, b) => a.min - b.min);
  
    // Insertar el precio base como primer tramo
    tiers.unshift({ min: 0, price: item.price });
  
    // Buscar el tramo correspondiente a la cantidad
    let unitPrice = item.price;
  
    for (let i = 0; i < tiers.length; i++) {
      const current = tiers[i];
      const next = tiers[i + 1];
  
      if (!next || totalQty < next.min) {
        unitPrice = current.price;
        break;
      }
    }
  
    const totalPrice = totalQty * unitPrice;
  
    return (
      <div className="pt-total-box">
        <div className="pt-total-title">🧵 Total:</div>
        <div className="pt-total-line">
          {totalQty} kg{totalQty !== 1 ? 's' : ''} × ${unitPrice.toLocaleString('de-DE')}
        </div>
        <div className="pt-total-amount">${totalPrice.toLocaleString('de-DE')}</div>
      </div>
    );
  };
  
  const renderMeasurePriceTable = () => {
    const stockDescriptions = item.stock_product_descriptions || [];
  
    const measureTiers = stockDescriptions
      .filter((desc) => {
        const name = desc.stock_descriptions?.name;
        return name && !isNaN(parseFloat(name));
      })
      .map((desc) => ({
        min: parseFloat(desc.stock_descriptions.name),
        price: desc.price
      }))
      .sort((a, b) => a.min - b.min);
  
    const tiersWithLabels = [];
  
    if (measureTiers.length > 0) {
      if(measureTiers[0]?.min > 1){
        // Primer tramo: precio base
        tiersWithLabels.push({
          label: `Hasta ${measureTiers[0].min} ${unitLabel}`,
          price: item.price
        });
      }
  
      // Resto de los tramos
      for (let i = 0; i < measureTiers.length; i++) {
        const tier = measureTiers[i];
        tiersWithLabels.push({
          label: `+${tier.min} ${unitLabel}`,
          price: tier.price
        });
      }
    } else {
      // Si no hay medidas configuradas, mostrar solo precio base
      tiersWithLabels.push({
        label: `Precio por kg`,
        price: item.price
      });
    }
  
    return (
      <div className="pt-price-table">
        {tiersWithLabels.map((tier, i) => (
          <div key={i} className="pt-price-row">
            <span className="pt-price-label">{tier.label}</span>
            <span className="pt-price-value">${tier.price.toLocaleString('de-DE')}</span>
          </div>
        ))}
      </div>
    );
  };
  
  const renderProductQuantitySelector = () => {
    let hasColorOption = false;

    if (item.stock_product_descriptions.length > 0 && Object.keys(combinations).length > 0) {
      hasColorOption = true;

      return (
        <>
          <div className="product-details-stock-title">Seleccione un color</div>
          <ul className="product-details-stock">
            {Object.keys(combinations).map((id) => {
              const color = colors.find(({ stock_description_id }) => stock_description_id === Number(id));
              const descriptions = combinations[id];
              const isSelected = selectedColor === Number(id);

              return (
                <div
                  className={`product-details-stock-option ${isSelected ? 'product-selected' : ''}`}
                  key={`${item.id}-${id}-${wholesale.id}`}
                >
                  <DescriptionItem
                    description={color}
                    handleColorSelect={handleColorSelect}
                    onQuantityChange={onQuantityChange}
                    showQuantitySelector={descriptions.length === 0}
                    storage={cartItemDescriptions.find(
                      (data) => data.descriptionId === color.id && data.wholesale.id === wholesale.id
                    )}
                    storageSub={cartItemSubDescriptions.find(
                      (data) => data.descriptionId === color.id && data.wholesale.id === wholesale.id
                    )}
                    othersWholesale={cartItemDescriptions.filter(
                      (data) => data.descriptionId === color.id && data.wholesale.id !== wholesale.id
                    )}
                    othersWholesaleSub={cartItemSubDescriptions.filter(
                      (data) => data.descriptionId === color.id && data.wholesale.id !== wholesale.id
                    )}
                  />
                </div>
              );
            })}
          </ul>

          {selectedColor !== null && (
            <ul className="product-details-stock-option product-details-stock-description">
              {combinations[selectedColor].map((description) => {
                const stockOK =
                  (!wholesale.bool && description.stock > 0) ||
                  (wholesale.bool && description.stock >= wholesale.quantity);

                return stockOK ? (
                  <li className="float-xs-left" key={`${item.id}-${description.id}-${wholesale.id}`}>
                    <DescriptionItem
                      description={description}
                      onQuantityChange={onQuantityChange}
                      showQuantitySelector={true}
                      showColors={true}
                      storage={cartItemDescriptions.find(
                        (data) => data.descriptionId === description.id && data.wholesale.id === wholesale.id
                      )}
                      storageSub={cartItemSubDescriptions.find(
                        (data) => data.descriptionId === description.id && data.wholesale.id === wholesale.id
                      )}
                      othersWholesale={cartItemDescriptions.filter(
                        (data) => data.descriptionId === description.id && data.wholesale.id !== wholesale.id
                      )}
                      othersWholesaleSub={cartItemSubDescriptions.filter(
                        (data) => data.descriptionId === description.id && data.wholesale.id !== wholesale.id
                      )}
                    />
                  </li>
                ) : null;
              })}
            </ul>
          )}
        </>
      );
    }

    const quantity = productCart ? productCart.quantity : (productCartSub?.quantity || 0);
    const otherProducts = cartItemDescriptions.filter(p => p.wholesale?.id !== wholesale.id);

    return (
      <QuantitySelector
        limit={item.stock}
        onQuantityChange={onQuantityChange}
        quantity={quantity}
        quantityOthersWholesale={otherProducts.map(p => p.quantity)}
        show={show}
        isTextil= {true}
        unit_type={item?.unit_type}
      />
    );
  };

  const renderAddButton = () => (
    <div className="pt-add-wrap">
      
    </div>
  );

  return (
    <div className="product-textil-page" data-testid="product-textil">
      <div className="pt-container">
        <div className="pt-grid">
          {/* Galería */}
          <section className="pt-gallery">
            <div className="pt-image-main">
              {photoList[active] ? (
                <Zoom>
                  <img src={photoList[active]} alt={item?.name} />
                </Zoom>
              ) : <div className="pt-image-placeholder" />}
            </div>

            {photoList.length > 1 && (
              <div className="pt-thumbs" role="listbox" aria-label="Miniaturas">
                <button
                  type="button"
                  className="pt-thumb-nav"
                  aria-label="Anterior"
                  onClick={() => setActive((i) => Math.max(0, i - 1))}
                  disabled={active === 0}
                >
                  ‹
                </button>

                <div className="pt-thumbs-track" tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowRight') setActive((i) => Math.min(photoList.length - 1, i + 1));
                      if (e.key === 'ArrowLeft')  setActive((i) => Math.max(0, i - 1));
                    }}>
                  {photoList.map((src, idx) => (
                    <button
                      key={idx}
                      role="option"
                      aria-selected={idx === active}
                      className={`pt-thumb ${idx === active ? 'is-active' : ''}`}
                      onClick={() => setActive(idx)}
                      type="button"
                      title={`Ver imagen ${idx + 1}`}
                    >
                      <img src={src} alt={`Vista ${idx + 1}`} loading="lazy" decoding="async" />
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="pt-thumb-nav"
                  aria-label="Siguiente"
                  onClick={() => setActive((i) => Math.min(photoList.length - 1, i + 1))}
                  disabled={active === photoList.length - 1}
                >
                  ›
                </button>
              </div>
            )}

          </section>

          {/* Info */}
          <section className="pt-info">
            <div className="pt-header">
              <div className="pt-subtitle">{item.categories?.[0]?.name?.toUpperCase()}</div>
              <h1 className="pt-title">{item.name}</h1>
            </div>
            {(item?.description && String(item.description).trim().length > 0) && (
              <div className="pt-desc-box" role="region" aria-label="Descripción del producto">
                <h4 className="pt-desc-title">Descripción</h4>
                <div
                  className="pt-desc-body"
                  // Si viene HTML desde el backend, lo respetamos:
                  dangerouslySetInnerHTML={{ __html: item.description }}
                />
              </div>
            )}
            <div className="pt-price-tiers">
              <h4>Precios por cantidad:</h4>
              <ul>
                {item.measure_tiers?.map((tier, i) => (
                  <li key={i}>
                    A partir de {tier.min}mt{tier.max ? ` hasta ${tier.max}mt` : ''}: 
                    <span>${tier.price.toLocaleString('de-DE')}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="pt-select">
              {renderMeasurePriceTable()}
              {renderProductQuantitySelector()}
              {renderTotal()}
              {hasInstallments && showMPButton && (
                <div 
                  className="pt-payment-methods-link" 
                  onClick={onOpenPaymentModal}
                  style={{ marginTop: '8px', cursor: 'pointer', color: '#3483fa', fontSize: '14px', fontWeight: '500' }}
                >
                  Ver bancos con cuotas sin interés
                </div>
              )}
              {renderAddButton()}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

ProductTextil.propTypes = {
  item: productPropTypes.isRequired,
  cartItemDescriptions: PropTypes.array.isRequired,
  cartItemSubDescriptions: PropTypes.array.isRequired,
  onProductClick: PropTypes.func,
  onProductCopyClick: PropTypes.func,
  onQuantityChange: PropTypes.func.isRequired,
  singleProductFullUrl: PropTypes.string,
  colors: PropTypes.array.isRequired,
  combinations: PropTypes.object.isRequired,
  wholesale: PropTypes.object.isRequired,
  setWholesale: PropTypes.func.isRequired,
  productCart: PropTypes.object,
  productCartSub: PropTypes.object,
  handleColorSelect: PropTypes.func.isRequired,
  talles: PropTypes.array,
  combinationsTalle: PropTypes.array,
  selectedColor: PropTypes.number,
  getValidPriceRange: PropTypes.func,
  show: PropTypes.bool,
  onClose: PropTypes.func
};

export default ProductTextil;
